<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { usePage } from "@inertiajs/inertia-vue3";
import {
  Chart,
  RadarController,
  BarController,
  LineElement,
  PointElement,
  BarElement,
  RadialLinearScale,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// Registrar componentes necesarios de Chart.js
Chart.register(
  RadarController,
  BarController,
  LineElement,
  PointElement,
  BarElement,
  RadialLinearScale,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler
);

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  isBar: {
    type: Boolean,
    default: null,
  },
  showLegend: {
    type: Boolean,
    default: false,
  },
  heightClass: {
    type: String,
    default: "h-[340px] xl:h-[380px]",
  },
});

const page = usePage();
const confGlobal = computed(() => page.props.value.confGlobal || {});

// Determinar si se usa gráfica de barras
const useBarChart = computed(() => {
  if (props.isBar !== null && props.isBar !== undefined) {
    return Boolean(props.isBar);
  }
  const val = confGlobal.value.STUDENT_COMPETENCY_GRAPHIC_IS_BAR;
  return val === "1" || val === 1 || val === true;
});

const canvasRef = ref(null);
let chartInstance = null;

const getColorForValue = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return "#2563EB";
  if (num < 11) return "#EF4444"; // Menor a 11: Rojo
  return "#2563EB"; // 11 a 20: Azul
};

const getBgColorForValue = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return "rgba(37, 99, 235, 0.85)";
  if (num < 11) return "rgba(239, 68, 68, 0.85)";
  return "rgba(37, 99, 235, 0.85)";
};

const formatChartData = (sourceData, isBar) => {
  if (!sourceData) {
    return { labels: [], datasets: [] };
  }

  const rawLabels = sourceData.labels || sourceData.label || [];
  const rawPoints = Array.isArray(sourceData.data)
    ? sourceData.data
    : (sourceData.datasets?.[0]?.data || []);

  if (isBar) {
    // Emparejar y ordenar de menor a mayor por promedio
    const paired = rawLabels.map((label, index) => ({
      label,
      value: parseFloat(rawPoints[index]) || 0,
    }));

    paired.sort((a, b) => a.value - b.value);

    const sortedLabels = paired.map((item) => item.label);
    const sortedPoints = paired.map((item) => item.value);
    const pointColors = sortedPoints.map((val) => getColorForValue(val));
    const bgColors = sortedPoints.map((val) => getBgColorForValue(val));

    return {
      labels: sortedLabels,
      datasets: [
        {
          label: "Promedio",
          data: sortedPoints,
          backgroundColor: bgColors,
          borderColor: pointColors,
          borderWidth: 1.5,
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 45,
        },
      ],
    };
  }

  // Radar
  const pointColors = rawPoints.map((val) => getColorForValue(val));

  return {
    labels: rawLabels,
    datasets: [
      {
        label: "Promedio",
        data: rawPoints,
        fill: true,
        borderColor: "#3B82F6",
        backgroundColor: "rgba(59, 130, 246, 0.15)",
        borderWidth: 2,
        pointBackgroundColor: pointColors,
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointHoverBackgroundColor: pointColors,
        pointHoverBorderColor: "#FFFFFF",
        pointHoverBorderWidth: 2,
      },
    ],
  };
};

const buildChartOptions = (isBar) => {
  const tooltipCallback = {
    label: function (context) {
      const val = context.raw;
      let estado = "Destacado";
      if (val < 11) estado = "Desaprobado (< 11)";
      else if (val <= 15) estado = "Regular (11 - 15)";
      else estado = "Destacado (> 15)";
      return ` Promedio: ${val} - ${estado}`;
    },
  };

  if (isBar) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      resizeDelay: 100,
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          max: 20,
          ticks: {
            stepSize: 4,
            font: { size: 11, weight: "600" },
            color: "#64748B",
          },
          grid: {
            color: "rgba(226, 232, 240, 0.7)",
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 40,
            minRotation: 0,
            font: { size: 11, weight: "600" },
            color: "#334155",
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          padding: 10,
          cornerRadius: 8,
          callbacks: tooltipCallback,
        },
      },
    };
  }

  // Opciones Radar
  return {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 100,
    scales: {
      r: {
        grid: {
          circular: true,
          color: "rgba(156, 163, 175, 0.25)",
        },
        angleLines: {
          color: "rgba(156, 163, 175, 0.25)",
        },
        pointLabels: {
          font: {
            size: 11,
            weight: "600",
          },
          color: "#4B5563",
        },
        ticks: {
          stepSize: 4,
          backdropColor: "transparent",
          font: {
            size: 10,
          },
        },
        beginAtZero: true,
        min: 0,
        max: 20,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        padding: 10,
        cornerRadius: 8,
        callbacks: tooltipCallback,
      },
    },
  };
};

const createOrUpdateChart = () => {
  if (!canvasRef.value) return;

  const isBar = useBarChart.value;
  const chartType = isBar ? "bar" : "radar";
  const formattedData = formatChartData(props.data, isBar);
  const options = buildChartOptions(isBar);

  if (chartInstance) {
    // Si cambió el tipo de gráfico, destruir y recrear
    if (chartInstance.config.type !== chartType) {
      chartInstance.destroy();
      chartInstance = new Chart(canvasRef.value, {
        type: chartType,
        data: formattedData,
        options: options,
      });
      return;
    }

    // Actualizar datos
    chartInstance.data.labels = formattedData.labels;
    chartInstance.data.datasets = formattedData.datasets;
    chartInstance.options = options;
    chartInstance.update();
    return;
  }

  // Crear nueva instancia
  chartInstance = new Chart(canvasRef.value, {
    type: chartType,
    data: formattedData,
    options: options,
  });
};

onMounted(() => {
  createOrUpdateChart();
});

watch(
  () => [props.data, useBarChart.value],
  () => {
    createOrUpdateChart();
  },
  { deep: true }
);

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
});
</script>

<template>
  <div class="w-full flex flex-col justify-between">
    <!-- Contenedor del Canvas -->
    <div :class="['relative w-full', heightClass]">
      <canvas ref="canvasRef" />
    </div>

    <!-- Leyenda de colores de rendimiento -->
    <div
      v-if="showLegend"
      class="flex items-center justify-center gap-6 py-2.5 mt-2 border-t border-slate-100 dark:border-slate-800 text-xs font-semibold text-slate-600 dark:text-slate-300"
    >
      <div class="flex items-center gap-2">
        <span class="w-3 h-3 rounded-full bg-red-500 ring-2 ring-red-100 inline-block"></span>
        <span>&lt; 11 Desaprobado</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="w-3 h-3 rounded-full bg-blue-600 ring-2 ring-blue-100 inline-block"></span>
        <span>&ge; 11 Aprobado</span>
      </div>
    </div>
  </div>
</template>
